import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, CircularProgress, Alert, Divider
} from '@mui/material';
import { Sparkles, X, Plus, Pencil, Trash2, Check, XCircle } from 'lucide-react';
import AdminModal from './AdminModal';
import { API_BASE_URL } from '../utils/api';

function FactRow({ fact, onDelete }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.5, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', flex: 1, lineHeight: 1.4 }}>
        {fact.text}
      </Typography>
      <IconButton size="small" onClick={() => onDelete(fact.factId)} sx={{ p: 0.3, color: 'rgba(248,113,113,0.6)', '&:hover': { color: '#fca5a5' }, flexShrink: 0, mt: -0.25 }}>
        <X size={12} />
      </IconButton>
    </Box>
  );
}

function ProposalRow({ proposal }) {
  const opColors = { add: '#4ade80', update: '#facc15', delete: '#f87171' };
  const opLabels = { add: 'Add', update: 'Edit', delete: 'Remove' };
  const OpIcon = proposal.op === 'add' ? Plus : proposal.op === 'update' ? Pencil : Trash2;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, px: 1 }}>
      <Chip
        icon={<OpIcon size={10} />}
        label={opLabels[proposal.op] || proposal.op}
        size="small"
        sx={{ bgcolor: `${opColors[proposal.op]}22`, color: opColors[proposal.op], fontWeight: 600, fontSize: '0.65rem', height: 20, '& .MuiChip-icon': { color: 'inherit' } }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {proposal.text && (
          <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', lineHeight: 1.4 }}>
            {proposal.text}
          </Typography>
        )}
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.3 }}>
          {proposal.reason}
        </Typography>
      </Box>
    </Box>
  );
}

export default function SpeechAnalysisModal({ open, onClose, character, getToken, onCharacterUpdate }) {
  const [speechFacts, setSpeechFacts] = useState([]);
  const [mcSpeechFacts, setMcSpeechFacts] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [proposals, setProposals] = useState(null); // { characterFactProposals, mcFactProposals, summary }
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (character) {
      setSpeechFacts(character.speechFacts || []);
      setMcSpeechFacts(character.mcSpeechFacts || []);
      setProposals(null);
      setError('');
    }
  }, [character]);

  const deleteFact = useCallback(async (factId, listType) => {
    if (!character) return;
    try {
      const token = await getToken();
      const newSpeech = listType === 'speech'
        ? speechFacts.filter((f) => f.factId !== factId)
        : speechFacts;
      const newMc = listType === 'mc'
        ? mcSpeechFacts.filter((f) => f.factId !== factId)
        : mcSpeechFacts;

      const res = await fetch(`${API_BASE_URL}/api/characters/${character.id}/facts`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ speechFacts: newSpeech, mcSpeechFacts: newMc })
      });
      if (!res.ok) throw new Error('Failed to update');
      setSpeechFacts(newSpeech);
      setMcSpeechFacts(newMc);
    } catch (e) {
      setError(e.message);
    }
  }, [character, getToken, speechFacts, mcSpeechFacts]);

  const runAnalysis = useCallback(async () => {
    if (!character) return;
    setAnalyzing(true);
    setError('');
    setProposals(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/characters/${character.id}/analyze-speech`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId: character.teamId })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Analysis failed');
      }
      const data = await res.json();
      setProposals(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalyzing(false);
    }
  }, [character, getToken]);

  const applyProposals = useCallback(async () => {
    if (!character || !proposals) return;
    setApplying(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/characters/${character.id}/apply-speech-proposals`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          characterFactProposals: proposals.characterFactProposals,
          mcFactProposals: proposals.mcFactProposals
        })
      });
      if (!res.ok) throw new Error('Failed to apply');
      const data = await res.json();
      setSpeechFacts(data.speechFacts || []);
      setMcSpeechFacts(data.mcSpeechFacts || []);
      setProposals(null);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (e) {
      setError(e.message);
    } finally {
      setApplying(false);
    }
  }, [character, proposals, getToken, onCharacterUpdate]);

  const hasProposals = proposals &&
    ((proposals.characterFactProposals?.length || 0) + (proposals.mcFactProposals?.length || 0)) > 0;

  if (!character) return null;

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={`AI Speech Analysis — ${character.name}`}
      maxWidth="sm"
      actions={
        proposals ? (
          <>
            <Button onClick={() => setProposals(null)} disabled={applying}>Dismiss</Button>
            <Button variant="contained" startIcon={<Check size={14} />} onClick={applyProposals} disabled={applying || !hasProposals}>
              {applying ? 'Applying...' : 'Apply All'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            startIcon={analyzing ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <Sparkles size={14} />}
            onClick={runAnalysis}
            disabled={analyzing}
          >
            {analyzing ? 'Analyzing...' : 'Run Analysis'}
          </Button>
        )
      }
    >
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Proposals overlay */}
      {proposals && (
        <Box sx={{ mb: 2 }}>
          {proposals.summary && (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1, fontStyle: 'italic' }}>
              {proposals.summary}
            </Typography>
          )}
          {!hasProposals && (
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', textAlign: 'center', py: 2 }}>
              No changes proposed — current facts look good.
            </Typography>
          )}
          {proposals.characterFactProposals?.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.light', px: 1 }}>
                Character Speech — Proposed Changes
              </Typography>
              {proposals.characterFactProposals.map((p, i) => <ProposalRow key={i} proposal={p} />)}
            </Box>
          )}
          {proposals.mcFactProposals?.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'info.light', px: 1 }}>
                MC Speech — Proposed Changes
              </Typography>
              {proposals.mcFactProposals.map((p, i) => <ProposalRow key={i} proposal={p} />)}
            </Box>
          )}
        </Box>
      )}

      {/* Current facts */}
      {!proposals && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.light', display: 'block', mb: 0.5, px: 1 }}>
              Character Speech Notes ({speechFacts.length})
            </Typography>
            {speechFacts.length === 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', px: 1, py: 1 }}>
                No notes yet. Run analysis to generate.
              </Typography>
            ) : (
              speechFacts.map((f) => (
                <FactRow key={f.factId} fact={f} onDelete={(id) => deleteFact(id, 'speech')} />
              ))
            )}
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'info.light', display: 'block', mb: 0.5, px: 1 }}>
              MC Speech Notes ({mcSpeechFacts.length})
            </Typography>
            {mcSpeechFacts.length === 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', px: 1, py: 1 }}>
                No notes yet. Run analysis to generate.
              </Typography>
            ) : (
              mcSpeechFacts.map((f) => (
                <FactRow key={f.factId} fact={f} onDelete={(id) => deleteFact(id, 'mc')} />
              ))
            )}
          </Box>
        </>
      )}
    </AdminModal>
  );
}
